import { useEffect, useMemo, useRef, useState } from "react";
import {
  X, Trash2, MessageSquarePlus, LogOut, Brain, Shield, KeyRound,
  CreditCard, Activity, Search, FolderPlus, Folder, ChevronDown, ChevronRight,
  Pencil, Lock, MoreVertical, HelpCircle, Briefcase, Mic, FileText, ShieldCheck, Network,
  BarChart3,
} from "lucide-react";
import { Link } from "react-router-dom";
import ExportMenu from "@/components/ExportMenu";
import { canExport } from "@/lib/exportAccess";
import { API } from "@/lib/api";

/**
 * HistoryDrawer — dual-mode. On mobile (<lg) it slides in as an overlay
 * drawer. On desktop (lg+) the same component is rendered pinned as a
 * persistent left sidebar. The variant is chosen by the parent via the
 * `pinned` prop; visibility across breakpoints is handled by wrapper
 * classes on the parent (`hidden lg:flex` for pinned, `lg:hidden` for
 * the drawer).
 */
export default function HistoryDrawer({
  open,
  onClose,
  conversations,
  activeId,
  onSelect,
  onDelete,
  onClear,
  onNew,
  user,
  onLogout,
  memoryEnabled,
  onToggleMemory,
  // Projects
  pinned = false,
  projects = [],
  projectsAvailable = false,
  activeProjectId = null,
  onSelectProject,   // (project | null) => void — null means "all chats"
  onCreateProject,   // () => void — opens a modal owned by parent
  onEditProject,     // (project) => void
  onDeleteProject,   // (project) => void
}) {
  const isLoggedIn = user && typeof user === "object";
  const showExport = canExport(user);
  const apiKeysEnabled = !!(user && user.api_keys_enabled);
  const isAdmin = user && user.role === "admin";

  const [query, setQuery] = useState("");
  const [projectsExpanded, setProjectsExpanded] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [canPredictmarkets, setCanPredictmarkets] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (!isAdmin) {
      setCanPredictmarkets(false);
      return () => { cancelled = true; };
    }
    fetch(`${API}/predictmarkets/status`, { credentials: "include" })
      .then((res) => { if (!cancelled) setCanPredictmarkets(res.ok); })
      .catch(() => { if (!cancelled) setCanPredictmarkets(false); });
    return () => { cancelled = true; };
  }, [isAdmin, user?.email]);

  // Close user menu on outside click.
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDoc = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [userMenuOpen]);

  const userInitial = user
    ? ((user.name || user.email || "?").trim().charAt(0) || "?").toUpperCase()
    : "?";

  const closeIfDrawer = () => {
    if (!pinned) onClose && onClose();
    setUserMenuOpen(false);
  };

  // Filter conversations by (a) active project scope and (b) search query.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return conversations.filter((c) => {
      if (activeProjectId && c.projectId !== activeProjectId) return false;
      if (activeProjectId === null && c.projectId) {
        // "All chats" (null) shows everything. "Unfiled" is a separate view we
        // don't ship yet — for now, null == all.
      }
      if (!q) return true;
      if ((c.title || "").toLowerCase().includes(q)) return true;
      const first = (c.messages || []).find((m) => m.role === "user");
      if (first && (first.content || "").toLowerCase().includes(q)) return true;
      return false;
    });
  }, [conversations, activeProjectId, query]);

  const activeProject = useMemo(
    () => (activeProjectId ? projects.find((p) => p.id === activeProjectId) : null),
    [projects, activeProjectId],
  );

  // Shared inner content — used in both drawer and pinned modes.
  const inner = (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-between px-6 py-5 border-b border-[var(--border)]">
        <div className="font-mono-ui text-[11px] tracking-[0.28em] uppercase text-[var(--text)]">
          {activeProject ? activeProject.name : "Conversations"}
        </div>
        {!pinned && (
          <button
            onClick={onClose}
            className="text-[var(--text-muted)] hover:text-[var(--accent)] transition-colors"
            data-testid="history-close"
            aria-label="Close history"
          >
            <X size={16} />
          </button>
        )}
      </div>

      <button
        onClick={() => {
          onNew();
          if (!pinned) onClose && onClose();
        }}
        className="mx-4 mt-4 px-4 py-3 border border-[var(--border)] hover:border-[var(--accent)] hover:text-[var(--accent)] text-[var(--text)] font-mono-ui text-[11px] tracking-[0.24em] uppercase transition-colors inline-flex items-center gap-2 justify-center"
        data-testid="history-new"
      >
        <MessageSquarePlus size={13} />
        New chat
      </button>

      {/* Search */}
      {isLoggedIn && conversations.length > 0 && (
        <div className="mx-4 mt-4 relative">
          <Search
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-dim)]"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats"
            data-testid="history-search"
            className="w-full pl-9 pr-3 py-2 bg-transparent border border-[var(--border)] focus:border-[var(--accent)] outline-none font-ui text-sm text-[var(--text)] placeholder:text-[var(--text-dim)] transition-colors"
          />
        </div>
      )}

      {/* Projects section */}
      {isLoggedIn && (
        <div className="mx-2 mt-5">
          <div className="px-2 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setProjectsExpanded((v) => !v)}
              className="inline-flex items-center gap-1.5 font-mono-ui text-[10px] tracking-[0.28em] uppercase text-[var(--text-dim)] hover:text-[var(--text)] transition-colors"
              data-testid="projects-toggle"
            >
              {projectsExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
              Projects
            </button>
            {projectsAvailable ? (
              <button
                type="button"
                onClick={onCreateProject}
                className="text-[var(--text-dim)] hover:text-[var(--accent)] transition-colors p-1"
                data-testid="project-create"
                title="New project"
                aria-label="New project"
              >
                <FolderPlus size={13} />
              </button>
            ) : (
              <Link
                to="/account/billing"
                onClick={pinned ? undefined : onClose}
                className="text-[var(--text-dim)] hover:text-[var(--accent)] transition-colors p-1"
                title="Projects — upgrade to Pro"
                data-testid="project-upgrade"
              >
                <Lock size={12} />
              </Link>
            )}
          </div>

          {projectsExpanded && (
            <ul className="mt-2">
              {/* All chats pseudo-project */}
              <li>
                <button
                  type="button"
                  onClick={() => onSelectProject && onSelectProject(null)}
                  className={`w-full text-left px-3 py-2 rounded flex items-center gap-2 transition-colors group ${
                    activeProjectId === null
                      ? "bg-[var(--bg)] text-[var(--text)]"
                      : "text-[var(--text-muted)] hover:bg-[var(--bg)] hover:text-[var(--text)]"
                  }`}
                  data-testid="project-all"
                >
                  <Folder size={13} className="shrink-0 opacity-70" />
                  <span className="font-ui text-sm truncate">All chats</span>
                </button>
              </li>

              {projectsAvailable && projects.map((p) => (
                <li key={p.id} className="group">
                  <div
                    className={`px-3 py-2 rounded flex items-center gap-2 transition-colors ${
                      activeProjectId === p.id
                        ? "bg-[var(--bg)] text-[var(--text)]"
                        : "text-[var(--text-muted)] hover:bg-[var(--bg)] hover:text-[var(--text)]"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => onSelectProject && onSelectProject(p)}
                      className="flex-1 min-w-0 text-left inline-flex items-center gap-2"
                      data-testid={`project-item-${p.id}`}
                    >
                      <Folder size={13} className="shrink-0 opacity-70" />
                      <span className="font-ui text-sm truncate">{p.name}</span>
                    </button>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditProject && onEditProject(p);
                        }}
                        className="text-[var(--text-dim)] hover:text-[var(--accent)] transition-colors p-0.5"
                        data-testid={`project-edit-${p.id}`}
                        aria-label={`Edit ${p.name}`}
                        title="Edit project"
                      >
                        <Pencil size={11} />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteProject && onDeleteProject(p);
                        }}
                        className="text-[var(--text-dim)] hover:text-red-400 transition-colors p-0.5"
                        data-testid={`project-delete-${p.id}`}
                        aria-label={`Delete ${p.name}`}
                        title="Delete project"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}

              {!projectsAvailable && (
                <li className="mt-1">
                  <Link
                    to="/account/billing"
                    onClick={pinned ? undefined : onClose}
                    className="block px-3 py-2 text-[var(--text-dim)] hover:text-[var(--text)] font-mono-ui text-[10px] tracking-[0.2em] uppercase transition-colors"
                    data-testid="project-upsell"
                  >
                    Upgrade to organize chats →
                  </Link>
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      {/* Conversations */}
      <div className="flex-1 overflow-y-auto scroll-warm mt-4 min-h-0">
        {filtered.length === 0 ? (
          <div className="px-6 py-10 font-mono-ui text-xs text-[var(--text-dim)] leading-relaxed">
            {conversations.length === 0
              ? "No conversations yet. Anything you ask gets saved here — in this browser only."
              : query
              ? `No chats match "${query}".`
              : activeProject
              ? `No chats in ${activeProject.name} yet. Start one — it'll pick up this project's instructions.`
              : "No conversations."}
          </div>
        ) : (
          <ul className="px-2">
            {filtered.map((c) => {
              const isActive = c.id === activeId;
              return (
                <li
                  key={c.id}
                  className={`group px-4 py-3 cursor-pointer transition-colors rounded ${
                    isActive
                      ? "bg-[var(--bg)] border-l-2 border-[var(--accent)]"
                      : "hover:bg-[var(--bg)]"
                  }`}
                  onClick={() => {
                    onSelect(c);
                    closeIfDrawer();
                  }}
                  data-testid={`history-item-${c.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-ui text-sm text-[var(--text)] line-clamp-2 leading-snug">
                        {c.kind === "voice" && (
                          <Mic
                            size={11}
                            className="inline-block mr-1.5 -mt-0.5 text-[var(--accent)] shrink-0"
                            aria-label="Voice session"
                          />
                        )}
                        {c.title}
                      </p>
                      <p className="font-mono-ui text-[10px] tracking-[0.18em] uppercase text-[var(--text-dim)] mt-2">
                        {new Date(c.updatedAt || c.createdAt).toLocaleString(undefined, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {" · "}
                        {c.messages.length} msg
                      </p>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {showExport && (
                        <span onClick={(e) => e.stopPropagation()}>
                          <ExportMenu
                            conversation={c}
                            anonymous={false}
                            testIdPrefix={`history-export-${c.id}`}
                          />
                        </span>
                      )}
                      <button
                        onClick={(ev) => {
                          ev.stopPropagation();
                          onDelete(c.id);
                        }}
                        className="text-[var(--text-dim)] hover:text-red-400 transition-colors"
                        data-testid={`history-delete-${c.id}`}
                        aria-label="Delete conversation"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {conversations.length > 0 && (
        <div className="border-t border-[var(--border)] px-6 py-2">
          <button
            onClick={onClear}
            className="font-mono-ui text-[9px] tracking-[0.28em] uppercase text-[var(--text-dim)] hover:text-red-400 transition-colors"
            data-testid="history-clear"
          >
            Clear all
          </button>
        </div>
      )}

      {user && (
        <div
          ref={userMenuRef}
          className="relative border-t border-[var(--border)]"
          data-testid="history-user-block"
        >
          {/* Popover — anchored above the pill */}
          {userMenuOpen && (
            <div
              className="absolute bottom-full left-3 right-3 mb-2 bg-[var(--bg-elevated)] border border-[var(--border)] shadow-2xl z-30 overflow-hidden"
              data-testid="user-menu-popover"
            >
              {/* Memory toggle row */}
              <button
                type="button"
                onClick={() => onToggleMemory(!memoryEnabled)}
                className="w-full px-4 py-3 flex items-center justify-between gap-3 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                data-testid="user-menu-memory"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <Brain size={13} className="text-[var(--text)]/70 shrink-0" />
                  <div className="text-left">
                    <div className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                      Memory
                    </div>
                    <div className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text)]/50 mt-0.5">
                      {memoryEnabled ? "On" : "Off"}
                    </div>
                  </div>
                </div>
                <span
                  role="switch"
                  aria-checked={!!memoryEnabled}
                  data-testid="memory-toggle"
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${
                    memoryEnabled ? "bg-white" : "bg-[var(--text)]/30"
                  }`}
                >
                  <span
                    className={`inline-block h-3.5 w-3.5 transform rounded-full bg-[var(--bg-elevated)] transition-transform ${
                      memoryEnabled ? "translate-x-5" : "translate-x-1"
                    }`}
                  />
                </span>
              </button>

              {isAdmin && (
                <Link
                  to="/admin"
                  onClick={closeIfDrawer}
                  data-testid="user-menu-admin"
                  className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                >
                  <Shield size={13} className="text-[var(--text)]/70 shrink-0" />
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                    Admin dashboard
                  </span>
                </Link>
              )}

              {isAdmin && (
                <Link
                  to="/admin/hbm-consult"
                  onClick={closeIfDrawer}
                  data-testid="user-menu-hbm-consult"
                  className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                >
                  <Briefcase size={13} className="text-[var(--text)]/70 shrink-0" />
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                    HBM Consult
                  </span>
                </Link>
              )}

              {isAdmin && (
                <Link
                  to="/hbm"
                  onClick={closeIfDrawer}
                  data-testid="user-menu-hbm-socials"
                  className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                >
                  <Network size={13} className="text-[var(--text)]/70 shrink-0" />
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                    HBM Socials
                  </span>
                </Link>
              )}

              {canPredictmarkets && (
                <Link
                  to="/admin/predict-markets"
                  onClick={closeIfDrawer}
                  data-testid="user-menu-predictmarkets"
                  className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                >
                  <BarChart3 size={13} className="text-[var(--text)]/70 shrink-0" />
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                    HBM - Predictmarkets
                  </span>
                </Link>
              )}

              {isAdmin && (
                <Link
                  to="/voice"
                  onClick={closeIfDrawer}
                  data-testid="user-menu-voice"
                  className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                >
                  <Mic size={13} className="text-[var(--text)]/70 shrink-0" />
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                    Voice Mode
                    <span className="ml-1.5 text-[9px] text-[var(--accent)]/70">BETA</span>
                  </span>
                </Link>
              )}

              {apiKeysEnabled && (
                <Link
                  to="/account/api-keys"
                  onClick={closeIfDrawer}
                  data-testid="user-menu-api-keys"
                  className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                >
                  <KeyRound size={13} className="text-[var(--text)]/70 shrink-0" />
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                    API keys
                  </span>
                </Link>
              )}

              {isAdmin && (
                <Link
                  to="/account/usage"
                  onClick={closeIfDrawer}
                  data-testid="user-menu-usage"
                  className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                >
                  <Activity size={13} className="text-[var(--text)]/70 shrink-0" />
                  <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                    Usage
                  </span>
                </Link>
              )}

              <Link
                to="/account/billing"
                onClick={closeIfDrawer}
                data-testid="user-menu-billing"
                className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
              >
                <CreditCard size={13} className="text-[var(--text)]/70 shrink-0" />
                <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                  Billing
                </span>
              </Link>

              <a
                href="mailto:support@humanebehavior.com"
                data-testid="user-menu-support"
                className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
                onClick={() => setUserMenuOpen(false)}
              >
                <HelpCircle size={13} className="text-[var(--text)]/70 shrink-0" />
                <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                  Support
                </span>
              </a>

              <Link
                to="/terms"
                onClick={closeIfDrawer}
                data-testid="user-menu-terms"
                className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
              >
                <FileText size={13} className="text-[var(--text)]/70 shrink-0" />
                <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                  Terms of Use
                </span>
              </Link>

              <Link
                to="/privacy"
                onClick={closeIfDrawer}
                data-testid="user-menu-privacy"
                className="px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors border-b border-[var(--border)]"
              >
                <ShieldCheck size={13} className="text-[var(--text)]/70 shrink-0" />
                <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                  Privacy Policy
                </span>
              </Link>

              <button
                type="button"
                onClick={() => {
                  setUserMenuOpen(false);
                  onLogout();
                }}
                data-testid="user-menu-logout"
                className="w-full px-4 py-3 flex items-center gap-2 hover:bg-[var(--text)]/10 transition-colors text-left"
              >
                <LogOut size={13} className="text-[var(--text)]/70 shrink-0" />
                <span className="font-mono-ui text-[10px] tracking-[0.24em] uppercase text-[var(--text)]">
                  Sign out
                </span>
              </button>
            </div>
          )}

          {/* Compact user pill */}
          <button
            type="button"
            onClick={() => setUserMenuOpen((v) => !v)}
            className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[var(--bg)] transition-colors"
            data-testid="user-pill"
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
          >
            <div
              className="w-8 h-8 rounded-full overflow-hidden bg-white text-[var(--accent)] flex items-center justify-center font-mono-ui text-sm font-bold shrink-0"
              data-testid="user-avatar"
            >
              {user.google_picture ? (
                <img
                  src={user.google_picture}
                  alt=""
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    // Google image sometimes 429s or expires — fall back
                    // to the initial silently.
                    e.currentTarget.replaceWith(
                      Object.assign(document.createElement("span"), {
                        textContent: userInitial,
                      }),
                    );
                  }}
                />
              ) : (
                userInitial
              )}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div
                className="font-ui text-sm text-[var(--text)] truncate flex items-center gap-1.5"
                data-testid="user-name"
              >
                {user.name || user.email.split("@")[0]}
                {isAdmin && (
                  <Shield size={10} className="text-[var(--accent)] shrink-0" aria-label="Admin" />
                )}
              </div>
              <div
                className="font-mono-ui text-[9px] tracking-[0.18em] uppercase text-[var(--text-dim)] truncate mt-0.5"
                data-testid="user-email"
              >
                {user.email}
              </div>
            </div>
            <MoreVertical size={14} className="text-[var(--text-dim)] shrink-0" />
          </button>
        </div>
      )}

      {!user && (
        <div
          className="border-t border-[var(--border)] px-4 py-4 space-y-2"
          data-testid="anon-cta-block"
        >
          <p className="font-mono-ui text-[9px] tracking-[0.24em] uppercase text-[var(--text-dim)] leading-relaxed">
            Sign in to sync across devices.
          </p>
          <div className="flex gap-2">
            <Link
              to="/register"
              onClick={pinned ? undefined : onClose}
              data-testid="drawer-create-account"
              className="flex-1 text-center py-1.5 font-mono-ui text-[10px] tracking-[0.24em] uppercase border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-[var(--bg)] transition-colors"
            >
              Create
            </Link>
            <Link
              to="/login"
              onClick={pinned ? undefined : onClose}
              data-testid="drawer-sign-in"
              className="flex-1 text-center py-1.5 font-mono-ui text-[10px] tracking-[0.24em] uppercase border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] hover:border-[var(--text)] transition-colors"
            >
              Sign in
            </Link>
          </div>
        </div>
      )}
    </div>
  );

  if (pinned) {
    // Persistent desktop sidebar. Static positioning, no backdrop, no
    // transform. Parent controls visibility via `hidden lg:flex`.
    return (
      <aside
        className="w-[300px] shrink-0 h-full bg-[var(--bg-elevated)] border-r border-[var(--border)] flex flex-col"
        data-testid="history-sidebar"
      >
        {inner}
      </aside>
    );
  }

  // Mobile drawer. Slides in over the content.
  return (
    <>
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-[var(--text)]/75 z-[90] transition-opacity lg:hidden ${
          open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="history-backdrop"
      />

      <aside
        className={`fixed left-0 top-0 h-full w-[min(92vw,380px)] bg-[var(--bg-elevated)] border-r border-[var(--border)] z-[100] shadow-2xl transition-transform duration-300 lg:hidden ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="history-drawer"
      >
        {inner}
      </aside>
    </>
  );
}
